import type { AnalyticsEventName, PropertiesFor } from '@syncanix/shared';

/**
 * SSR-safe instrumentation seam for the marketing site.
 *
 * The marketing pages are statically prerendered in Node (`entry-server`), so
 * they must NOT import `posthog-js` (it touches `window` and would break the
 * build). Call sites instead import the type-only `track()` from here, which
 * forwards to a sink registered at runtime by the client-only
 * MarketingAnalyticsProvider. On the server the sink is null → a no-op; in the
 * browser it forwards to the consent-gated PostHog wrapper (itself a no-op
 * until consent + init). One indirection keeps the SDK out of prerendered code.
 */

export interface AnalyticsSink {
  capture<N extends AnalyticsEventName>(name: N, properties?: PropertiesFor<N>): void;
}

let sink: AnalyticsSink | null = null;

/** Register (or clear) the runtime analytics sink. Called by the client provider. */
export function setAnalyticsSink(next: AnalyticsSink | null): void {
  sink = next;
}

/** Fire a typed marketing event. No-op on the server / before the sink is set. */
export function track<N extends AnalyticsEventName>(name: N, properties?: PropertiesFor<N>): void {
  sink?.capture(name, properties);
}
